import React, { useState } from 'react';
import { useLanguage } from './LanguageContext';
import { FaTimes, FaUser, FaPhone } from 'react-icons/fa';
import './ContactForm.css';

const ContactForm = ({ isOpen, onClose, onSubmit }) => {
    const { t } = useLanguage();
    const [formData, setFormData] = useState({
        name: '',
        phone: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Форматирование номера телефона
    const formatPhone = (value) => {
        // Удаляем все символы кроме цифр
        const numbers = value.replace(/\D/g, '');

        // Если начинается с 8, заменяем на 7
        let formatted = numbers;
        if (formatted.startsWith('8')) {
            formatted = '7' + formatted.slice(1);
        }

        // Форматируем как +7 (xxx) xxx-xx-xx
        if (formatted.startsWith('7')) {
            formatted = formatted.slice(1); // убираем 7
        }

        if (formatted.length >= 10) {
            return `+7 (${formatted.slice(0, 3)}) ${formatted.slice(3, 6)}-${formatted.slice(6, 8)}-${formatted.slice(8, 10)}`;
        } else if (formatted.length >= 6) {
            return `+7 (${formatted.slice(0, 3)}) ${formatted.slice(3, 6)}-${formatted.slice(6)}`;
        } else if (formatted.length >= 3) {
            return `+7 (${formatted.slice(0, 3)}) ${formatted.slice(3)}`;
        } else if (formatted.length > 0) {
            return `+7 (${formatted}`;
        }

        return '+7 (';
    };

    // Валидация телефона
    const validatePhone = (phone) => {
        const phoneRegex = /^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/;
        return phoneRegex.test(phone);
    };

    // Обработка изменений в полях
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'phone') {
            const formattedPhone = formatPhone(value);
            setFormData(prev => ({ ...prev, [name]: formattedPhone }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        // Очищаем ошибки при вводе
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Валидация формы
    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = t('nameRequired');
        }

        if (!formData.phone.trim()) {
            newErrors.phone = t('phoneRequired');
        } else if (!validatePhone(formData.phone)) {
            newErrors.phone = t('phoneInvalid');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Отправка формы
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            // Вызываем переданную функцию onSubmit
            await onSubmit(formData);

            // Очищаем форму
            setFormData({ name: '', phone: '' });
            setErrors({});

            // Закрываем форму
            onClose();
        } catch (error) {
        } finally {
            setIsSubmitting(false);
        }
    };

    // Закрытие формы
    const handleClose = () => {
        setFormData({ name: '', phone: '' });
        setErrors({});
        onClose();
    };

    // Если форма не открыта, не рендерим ничего
    if (!isOpen) return null;

    return (
        <div className="contact-form-overlay" onClick={handleClose}>
            <div className="contact-form-modal" onClick={(e) => e.stopPropagation()}>
                {/* Заголовок с кнопкой закрытия */}
                <div className="contact-form-header">
                    <div>
                        <h2 className="contact-form-title">{t('contactFormTitle')}</h2>
                        <p className="contact-form-subtitle">{t('contactFormSubtitle')}</p>
                    </div>
                    <button
                        className="contact-form-close"
                        onClick={handleClose}
                        aria-label={t('closeButton')}
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Форма */}
                <form onSubmit={handleSubmit} className="contact-form">
                    {/* Поле имени */}
                    <div className="form-field">
                        <label htmlFor="name" className="form-label">
                            <FaUser className="form-icon" />
                            {t('nameLabel')}
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder={t('namePlaceholder')}
                            className={`form-input ${errors.name ? 'error' : ''}`}
                        />
                        {errors.name && <span className="form-error">{errors.name}</span>}
                    </div>

                    {/* Поле телефона */}
                    <div className="form-field">
                        <label htmlFor="phone" className="form-label">
                            <FaPhone className="form-icon" />
                            {t('phoneLabel')}
                        </label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder={t('phonePlaceholder')}
                            className={`form-input ${errors.phone ? 'error' : ''}`}
                        />
                        {errors.phone && <span className="form-error">{errors.phone}</span>}
                    </div>

                    {/* Кнопки */}
                    <div className="form-actions">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="form-submit-btn"
                        >
                            {isSubmitting ? 'Отправка...' : t('submitButton')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ContactForm;
